import { useEffect, useRef, useState, type ReactElement } from 'react'
import { MoonIcon, SunIcon, SystemIcon } from './icons'

type Theme = 'light' | 'dark' | 'system'
const KEY = 'theme'

function readTheme(): Theme {
  try {
    const t = localStorage.getItem(KEY)
    return t === 'light' || t === 'dark' ? t : 'system'
  } catch { return 'system' }
}

/** Applies the choice to <html data-theme>; "system" removes it so the CSS
 *  follows prefers-color-scheme. The choice is a per-browser convenience in
 *  localStorage; index.html applies it before first paint. */
function applyTheme(t: Theme) {
  const root = document.documentElement
  if (t === 'system') delete root.dataset.theme
  else root.dataset.theme = t
  try { if (t === 'system') localStorage.removeItem(KEY); else localStorage.setItem(KEY, t) } catch { /* storage blocked */ }
}

const OPTIONS: { id: Theme; label: string; Icon: () => ReactElement }[] = [
  { id: 'light', label: 'Light', Icon: SunIcon },
  { id: 'dark', label: 'Dark', Icon: MoonIcon },
  { id: 'system', label: 'System', Icon: SystemIcon },
]

const HalfIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" /><path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" stroke="none" /></svg>
)

/** `short`: the button says "Theme"; `round`: a small circle with the half-filled icon alone, its name in the
 *  tooltip; otherwise it names the current choice. */
export function ThemeSwitch({ short = false, round = false }: { short?: boolean; round?: boolean } = {}) {
  const [theme, setTheme] = useState<Theme>(readTheme)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => { applyTheme(theme) }, [theme])
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc) }
  }, [open])
  const current = OPTIONS.find((o) => o.id === theme)!
  return (
    <div className="theme" ref={ref}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className={round ? 'round' : undefined} aria-label={round ? `Theme: ${current.label.toLowerCase()}` : undefined}
        title={`Theme: ${current.label.toLowerCase()}`}>
        {round ? <HalfIcon /> : <><current.Icon /> {short ? 'Theme' : `${current.label} theme`}</>}
      </button>
      {open ? (
        <div className="menu" role="menu">
          {OPTIONS.map(({ id, label, Icon }) => (
            <button key={id} type="button" role="menuitemradio" aria-checked={theme === id}
              onClick={() => { setTheme(id); setOpen(false) }}>
              <Icon /> {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
